import { useState } from 'react';
import { useT } from '../../i18n/locale-context';

/**
 * The server refuses a single term longer than this (`@MaxLength(64, { each:
 * true })` on `textTerms` / `excludeTerms`), so the cap has to be here, in the
 * commit — not only as the input's `maxLength` attribute.
 *
 * ⚠️ Measured 2026-10-03 (PostHog 01a0ff3f, Samsung Browser 30 on Android): a
 * term of ~100 characters, pasted from a Telegram message, sat in the chips as
 * if nothing was wrong, and Save answered with the server's raw «each value in
 * textTerms must be shorter than or equal to 64 characters». `maxLength` is not
 * enforced on text committed through an Android IME composition, which is how
 * both paste and voice input arrive there.
 */
export const TERM_MAX_CHARS = 64;

/**
 * A list of short strings, entered one at a time.
 *
 * Used for exclude terms and free-text terms — one component so the two cannot
 * drift in how they trim, deduplicate or cap. Both compile to the same kind of
 * clause on the server, so behaving differently here would be a lie about the
 * query.
 */
export function TagInput({
  value,
  onChange,
  placeholder,
  max = 10,
  ariaLabel,
}: {
  value: string[];
  onChange: (next: string[]) => void;
  placeholder?: string;
  max?: number;
  ariaLabel: string;
}) {
  const t = useT();
  const [draft, setDraft] = useState('');
  const full = value.length >= max;
  const tooLong = draft.trim().length > TERM_MAX_CHARS;

  /**
   * Turns the draft into a tag. Reachable from Enter, a trailing comma, AND
   * blur.
   *
   * ⚠️ Blur is the load-bearing one, and it was missing. A user typed «аренда»
   * into «Исключить слова», clicked Сохранить, and the word vanished: the draft
   * was never a tag, so the payload carried an empty array and the sheet saved a
   * search with no exclusions — silently, with no error and nothing on screen
   * saying Enter was required. Reported from production 2026-08-13 by a
   * subscriber who concluded the whole feature was broken.
   *
   * Blur fires before the Save button's click, so the committed term is in state
   * by the time the mutation reads it.
   */
  function commit(raw: string) {
    const term = raw.trim();
    // Blank and duplicate are both no-ops rather than errors: the user has not
    // made a mistake, they have simply not added anything.
    if (!term || value.includes(term) || full) return;
    // Too long stays in the box with the hint below it — cutting it to 64
    // behind the user's back would save a term they never wrote.
    if (term.length > TERM_MAX_CHARS) return;
    onChange([...value, term]);
    setDraft('');
  }

  return (
    <div>
      {value.length > 0 && (
        <ul className="mb-2 flex flex-wrap gap-1.5">
          {value.map((term) => (
            <li
              key={term}
              className="inline-flex items-center gap-1 rounded-md bg-brand-dim px-2 py-1 text-[12.5px] font-semibold text-brand"
            >
              {term}
              <button
                type="button"
                aria-label={t('tagInput.remove', { term })}
                onClick={() => onChange(value.filter((x) => x !== term))}
                className="text-[14px] leading-none text-brand/70 transition-colors hover:text-brand"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      <input
        aria-label={ariaLabel}
        type="text"
        value={draft}
        // The attribute stops the keyboard; `commit()` above stops the IME paths
        // that ignore it (see TERM_MAX_CHARS).
        maxLength={TERM_MAX_CHARS}
        disabled={full}
        placeholder={placeholder}
        onChange={(e) => {
          // A trailing comma commits — people paste and type lists that way.
          if (e.target.value.endsWith(',')) commit(e.target.value.slice(0, -1));
          else setDraft(e.target.value);
        }}
        onBlur={() => commit(draft)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            // The sheet wraps this in a <form>; Enter must add a tag, not save.
            e.preventDefault();
            commit(draft);
          }
          if (e.key === 'Backspace' && draft === '' && value.length > 0) {
            onChange(value.slice(0, -1));
          }
        }}
        className="h-11 w-full rounded-md border-[1.5px] border-edge bg-bone px-3 text-[16px] text-ink outline-none transition-colors placeholder:text-ink-muted focus:border-brand disabled:opacity-60"
      />
      {full && (
        <p className="mt-1 text-[12px] text-ink-muted">{t('tagInput.max', { count: max })}</p>
      )}
      {tooLong && (
        <p role="alert" className="mt-1 text-[12px] text-red-600">
          {t('tagInput.tooLong', { max: TERM_MAX_CHARS, count: draft.trim().length })}
        </p>
      )}
    </div>
  );
}
